import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { GripVertical, ArrowUpDown, ArrowUp, ArrowDown, Users } from "lucide-react";

const HR_REQUEST_TYPES = [
  { value: "new", label: "New Hire", color: "bg-green-500" },
  { value: "term", label: "Termination", color: "bg-red-500" },
  { value: "change", label: "Change", color: "bg-blue-500" },
  { value: "transfer", label: "Transfer", color: "bg-purple-500" },
  { value: "leave", label: "Leave", color: "bg-yellow-500" }
];

const renderHRCell = (request, columnId) => {
  const typeInfo = HR_REQUEST_TYPES.find(t => t.value === request.request_type) || HR_REQUEST_TYPES[0];

  switch (columnId) {
    case "request_number":
      return <span className="font-mono text-sm font-semibold text-slate-700 dark:text-white">{request.request_number || 'N/A'}</span>;
    case "created_at":
      return <span className="text-sm text-slate-600 dark:text-white">{new Date(request.created_at).toLocaleDateString()}</span>;
    case "type":
      return <Badge className={typeInfo.color + " text-white"}>{typeInfo.label}</Badge>;
    case "employee":
      return (
        <div className="max-w-xs">
          <div className="text-sm font-medium text-slate-800 dark:text-white truncate">
            {request.employee_first_name} {request.employee_last_name}
            {request.employee_suffix && ` ${request.employee_suffix}`}
          </div>
        </div>
      );
    case "dealership":
      return <span className="text-sm text-slate-600 dark:text-white">{request.dealership}</span>;
    case "assigned_to":
      return <span className="text-sm text-slate-600 dark:text-white">{request.assigned_to_name || "Unassigned"}</span>;
    case "start_date": {
      const dateParts = request.start_date ? request.start_date.split('-') : null;
      const displayDate = dateParts
        ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2]).toLocaleDateString()
        : 'N/A';
      return <span className="text-sm text-slate-600 dark:text-white">{displayDate}</span>;
    }
    case "status":
      return (
        <Badge className={
          request.status === "new" ? "bg-blue-100 text-blue-800" :
          request.status === "in_progress" ? "bg-purple-100 text-purple-800" :
          request.status === "pending" ? "bg-yellow-100 text-yellow-800" :
          request.status === "closed" ? "bg-gray-100 text-gray-800" :
          "bg-slate-100 text-slate-800"
        }>
          {request.status === "in_progress" ? "In Progress" : request.status.charAt(0).toUpperCase() + request.status.slice(1)}
        </Badge>
      );
    default:
      return null;
  }
};

export const HRRequestTable = ({
  hrColumns,
  setHRColumns,
  draggedHRColumn,
  setDraggedHRColumn,
  hrSortColumn,
  hrSortDirection,
  handleHRSort,
  activeHRRequests,
  saveHRColumnPreferences,
  navigate,
}) => {
  const handleHRDragStart = (e, columnId) => {
    setDraggedHRColumn(columnId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleHRDragOver = (e, columnId) => {
    e.preventDefault();
    if (draggedHRColumn === columnId) return;
  };

  const handleHRDrop = (e, targetColumnId) => {
    e.preventDefault();
    if (draggedHRColumn === targetColumnId) return;

    const newColumns = [...hrColumns];
    const draggedIndex = newColumns.findIndex(col => col.id === draggedHRColumn);
    const targetIndex = newColumns.findIndex(col => col.id === targetColumnId);
    const [removed] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, removed);

    setHRColumns(newColumns);
    saveHRColumnPreferences(newColumns);
    setDraggedHRColumn(null);
  };

  const visibleColumns = useMemo(() => hrColumns.filter(col => col.visible), [hrColumns]);

  return (
    <div className="bg-white dark:bg-[#2C2C2C] rounded-lg shadow-md overflow-hidden" data-testid="hr-table-view">
      <div className="px-4 py-2 bg-slate-100 dark:bg-[#343434] border-b border-slate-200 dark:border-slate-600">
        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <GripVertical className="w-3 h-3" />
          Drag columns to reorder - Click column name to sort
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-[#343434] border-b border-slate-200 dark:border-slate-600">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.id}
                  draggable
                  onDragStart={(e) => handleHRDragStart(e, column.id)}
                  onDragOver={(e) => handleHRDragOver(e, column.id)}
                  onDrop={(e) => handleHRDrop(e, column.id)}
                  className={`px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase tracking-wider cursor-move select-none hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors ${
                    draggedHRColumn === column.id ? "opacity-50 bg-blue-50 dark:bg-blue-900" : ""
                  }`}
                  style={{
                    width: column.id === 'request_number' ? '150px' : column.id === 'created_at' ? '110px' : 'auto'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-3 h-3 text-slate-400" />
                    <span
                      onClick={(e) => { e.stopPropagation(); handleHRSort(column.id); }}
                      className="flex items-center gap-1 cursor-pointer hover:text-[#7a1d42] dark:hover:text-pink-400"
                      data-testid={`sort-hr-${column.id}`}
                    >
                      {column.label}
                      {hrSortColumn === column.id ? (
                        hrSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-40" />
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-600">
            {activeHRRequests.length === 0 ? (
              <tr>
                <td colSpan={hrColumns.length} className="px-4 py-8 text-center text-slate-500 dark:text-white">
                  <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  No active HR requests found
                </td>
              </tr>
            ) : (
              activeHRRequests.map((request) => (
                <tr
                  key={request.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => navigate(`/hr-requests/${request.id}`)}
                  data-testid={`hr-table-row-${request.id}`}
                >
                  {visibleColumns.map((column) => (
                    <td
                      key={column.id}
                      className="px-4 py-4 whitespace-nowrap"
                      style={{
                        width: column.id === 'request_number' ? '150px' : column.id === 'created_at' ? '110px' : 'auto'
                      }}
                    >
                      {renderHRCell(request, column.id)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
